import {Component, Vue} from 'vue-property-decorator';
import { difference } from 'lodash';
import { mixins } from 'vue-class-component';
import UrlMixin from '@/components/query/url-mixin';
import MainMenuComplexTabsMetaMixin from '@/views/layout/navigation/mixins/main-menu-complex-tabs-meta-mixin';

export interface TabData {
  name: string;
  title: string;
  icon?: string;
  hideInMenu?: boolean;
  isLive?: boolean;
  meta?: any;
  showCondition?: () => boolean
}

export interface TabMeta {
  icon?: string;
}

@Component
export default class MainMenuComplexTabsMixin extends mixins(UrlMixin, MainMenuComplexTabsMetaMixin) {
  get campaignAsFirstTableFilterCondition() {
    return this.tableDrilldownFilter[0] && this.tableDrilldownFilter[0].filterTables === 'campaigns';
  }

  get trafficSourcesAsFirstTableFilterCondition() {
    return this.tableDrilldownFilter[0] && this.tableDrilldownFilter[0].filterTables === 'traffic-sources';
  }

  get customGroupsShowCondition() {
    return true;
    return this.isReport() && (this.campaignAsFirstTableFilterCondition || this.trafficSourcesAsFirstTableFilterCondition);
  }

  get customGroupsData() {
    return this.customKeys.map((key, index) => ({
      name: 'custom' + (index + 1),
      title: this.t('custom {0}', [index + 1]),
      meta: {
        customValue: this.$store.state.table.customGroups && this.$store.state.table.customGroups[key]
          ? this.$store.state.table.customGroups[key]
          : this.t('n/a')
      },
      showCondition: () => this.customGroupsShowCondition
    }));
  }

  get tableDrilldownFilter(): any[] {
    return this.$store.state.table.drilldownFilter || [];
  }

  get disableTreeViewList() {
    return [ 'ips', 'useragents', 'referrers' ];
  }

  get isTreeLevelsAllowed() {
    return !this.disableTreeViewList.includes(this.computedType);
  }

  get pComplexTabsData(): {[type: string]: TabData[]} {
    return {
      mainPlain: [
        {
          name: 'campaigns',
          icon: this.tabsMeta['campaigns'].icon,
          title: this.tabsMeta['campaigns'].title
        },
        {
          name: 'offers',
          icon: this.tabsMeta['offers'].icon,
          title: this.tabsMeta['offers'].title
        },
        {
          name: 'landings',
          icon: this.tabsMeta['landings'].icon,
          title: this.tabsMeta['landings'].title
        },
        {
          name: 'flows',
          icon: this.tabsMeta['flows'].icon,
          title: this.tabsMeta['flows'].title
        },
        {
          name: 'traffic-sources',
          icon: this.tabsMeta['traffic-sources'].icon,
          title: this.tabsMeta['traffic-sources'].title
        },
        {
          name: 'affiliate-networks',
          icon: this.tabsMeta['affiliate-networks'].icon,
          title: this.tabsMeta['affiliate-networks'].title
        },
      ],
      flow_rules_paths: [
        {
          name: 'flow_rule_paths',
          icon: this.tabsMeta['conversions'].icon,
          title: this.tabsMeta['flow_rule_paths'].title,
          showCondition: () => this.isReport() && this.$FF?.reportingFlowRulePath?.enabled
        },
        {
          name: 'flow_rules',
          icon: this.tabsMeta['conversions'].icon,
          title: this.tabsMeta['flow_rules'].title,
          showCondition: () => this.isReport() && this.$FF?.reportingFlowRulePath?.enabled
        },
      ],
      conversions: [
        {
          name: 'conversions',
          icon: this.tabsMeta['conversions'].icon,
          title: this.tabsMeta['conversions'].title,
          showCondition: () => !this.$store.state.table.isHideFilters
        },
      ],
      postbacks: [
        {
          name: 'postbacks',
          icon: this.tabsMeta['postbacks'].icon,
          title: this.t('Postbacks'),
          showCondition: () => {
            return (this.isReport()
              && !this.$store.state.table.isHideFilters
              && this.campaignAsFirstTableFilterCondition
              && this.tableDrilldownFilter.length <= 1)
              || !this.isReport();
          }
        },
      ],
      errors: [
        {
          name: 'errors',
          icon: this.tabsMeta['errors'].icon,
          title: this.t('Errors'),
          showCondition: () => {
            return (this.isReport()  
              && !this.$store.state.table.isHideFilters
              && this.campaignAsFirstTableFilterCondition 
              && this.tableDrilldownFilter.length <= 1) 
              || !this.isReport(); 
          }
        },
      ],
      browsers: [
        {
          name: 'default',
          title: this.t('Browsers'),
          hideInMenu: true
        },
        {
          name: 'browsers',
          title: this.t('Browser Names'),
        },
        {
          name: 'browser-versions',
          title: this.t('Browser Versions'),
        },
        {
          name: 'useragents',
          title: this.t('User Agents'),
          showCondition: () => this.isReport() && this.campaignAsFirstTableFilterCondition
        },
      ],
      oses: [
        {
          name: 'oses',
          title: this.t('OS'),
        },
        {
          name: 'os-versions',
          title: this.t('OS Versions'),
        },
      ],
      devices: [
        {
          name: 'default',
          title: this.t('Devices'),
          hideInMenu: true
        },
        {
          name: 'device-models',
          title: this.t('Device Models'),
        },
        {
          name: 'device-vendors',
          title: this.t('Device Vendors'),
        },
        {
          name: 'device-types',
          title: this.t('Device Types'),
        },
        {
          name: 'screen-resolutions',
          title: this.t('Screen Resolutions'),
        },
      ],
      isps: [
        {
          name: 'isps',
          title: this.t('ISP'),
        },
        {
          name: 'mobile-carriers',
          title: this.t('Mobile Carriers'),
        },
        {
          name: 'connection-types',
          title: this.t('Connection Types'),
        },
      ],
      countries: [
        {
          name: 'countries',
          title: this.t('Countries'),
          hideInMenu: true
        },
        {
          name: 'languages',
          title: this.t('Languages'),
        },
        {
          name: 'cities',
          title: this.t('Cities'),
        },
        {
          name: 'states',
          title: this.t('State / Region'),
        },
        {
          name: 'countries',
          title: this.t('Countries'),
        }
      ],
      lives: [
        {
          name: 'live-visits',
          title: this.t('Live Visits'),
          isLive: true,
          showCondition: () => this.isReport() && this.campaignAsFirstTableFilterCondition
        },
        {
          name: 'live-clicks',
          title: this.t('Live Clicks'),
          isLive: true,
          showCondition: () => this.isReport() && this.campaignAsFirstTableFilterCondition
        },
      ],
      referrers: [
        {
          name: 'referrers',
          title: this.t('Referrers'),
          showCondition: () => this.isReport() && this.campaignAsFirstTableFilterCondition
        },
        {
          name: 'referrer-domains',
          title: this.t('Referrer Domains'),
          showCondition: () => this.isReport()
        },
      ],
      ips: [
        {
          name: 'default',
          title: this.t('IPs'),
          hideInMenu: true,
          showCondition: () => !this.campaignAsFirstTableFilterCondition && this.isReport()
        },
        {
          name: 'ips',
          title: this.t('IPs'),
          showCondition: () => this.campaignAsFirstTableFilterCondition && this.isReport()
        },
        {
          name: 'subnet-small',
          title: this.t('Subnets (Small)'),
          showCondition: () => this.isReport()
        },
        {
          name: 'subnet-big',
          title: this.t('Subnets (Big)'),
          showCondition: () => this.isReport()
        }
      ],
      period: [
        {
          name: 'default',
          title: this.t('Period'),
          hideInMenu: true,
          showCondition: () => this.isReport()
        },
        {
          name: 'month',
          title: this.t('Month'),
          showCondition: () => this.isReport()
        },
        {
          name: 'day',
          title: this.t('Day of Month'),
          showCondition: () => this.isReport()
        },
        {
          name: 'day-of-week',
          title: this.t('Day Of Week'),
          showCondition: () => this.isReport()
        },
        {
          name: 'hour-of-day',
          title: this.t('Hour Of Day'),
          showCondition: () => this.isReport()
        },
      ],
      custom: this.customGroupsData
    }
  }

  get complexTabsData(): {[type: string]: TabData[]} {
    let keys = Object.keys(this.pComplexTabsData);
    let result: any = {};

    for(let key of keys) {
      let tabs = this.pComplexTabsData[key]
        .filter((tab) => (!tab.showCondition || tab.showCondition())
          && !this.isHidden(tab.name)
        );
      if(tabs.length) {
        result[key] = tabs;
      }
    }

    return result;
  }

  getTabsPlainTabsData(complexData:  {[type: string]: TabData[]}): {[type: string]: TabData} {
    let keys = Object.keys(complexData);
    let data: TabData[]  = [];

    for(let key of keys) {
      let actualData = complexData[key]
        .filter((item) => !item.hideInMenu);

      data.push(...actualData);
    }

    let result: {[type: string]: TabData} = {};
    data.forEach((item) => {
      result[item.name] = item;
    });

    return result;
  }

  get tabsPlainTabsData(): {[type: string]: TabData} {
    return this.getTabsPlainTabsData(this.complexTabsData)
  }

  get tabsPlainTabsDataBase(): {[type: string]: TabData} {
    return this.getTabsPlainTabsData(this.pComplexTabsData)
  }

  get tabsPlainTabsDataExclude(): string[] {
    let keys = Object.keys(this.tabsPlainTabsData);
    let keysBase = Object.keys(this.tabsPlainTabsDataBase);

    return difference(keysBase, keys);
  }

  get title() {
    let tabData = this.tabsPlainTabsData[this.computedType];
    if(tabData) {
      return tabData.title;
    }

    return '';
  }

  getTab(tabName: string) {
    if(!this.complexTabsData[tabName])
      return null;

    let tab: any = this.complexTabsData[tabName]
      .find((item: any) => item.name === this.computedType);

    if(!tab) {
      tab = this.complexTabsData[tabName][0];
      tab.isActive = false;
    } else {
      tab.isActive = true;
    }

    return tab;
  }


  get complexTabsBase() {
    const { drilldownFilter } = this.$store.state.table;
    const campaignAsFirstTableFilter = drilldownFilter && drilldownFilter.length && drilldownFilter[0].filterTables === 'campaigns';
    return [
      {
        name: 'countries',
        icon: this.tabsMeta['countries'].icon,
        data: this.getTab('countries'),
      },
      {
        name: 'isps',
        icon: this.tabsMeta['isps'].icon,
        data: this.getTab('isps'),
      },
      {
        name: 'ips',
        icon: this.tabsMeta['ips'].icon,
        data: this.getTab('ips'),
        showCondition: () => campaignAsFirstTableFilter
      },
      // {
      //   name: 'ipsReduced',
      //   icon: 'fas fa-exchange-alt',
      //   data: this.getTab('ipsReduced'),
      //   showCondition: () => !campaignAsFirstTableFilter
      // },
      {
        name: 'devices',
        icon: this.tabsMeta['devices'].icon,
        data: this.getTab('devices'),
      },
      {
        name: 'oses',
        icon: this.tabsMeta['oses'].icon,
        data: this.getTab('oses'),
      },
      {
        name: 'browsers',
        icon: this.tabsMeta['browsers'].icon,
        data: this.getTab('browsers'),
      },
      {
        name: 'referrers',
        icon: this.tabsMeta['referrers'].icon,
        data: this.getTab('referrers'),
        showCondition: () => this.isReport()
      },
      {
        name: 'lives',
        icon: this.tabsMeta['lives'].icon,
        data: this.getTab('lives'),
        showCondition: () => this.isReport()
      },
      {
        name: 'flow_rules_paths',
        icon: this.tabsMeta['flows'].icon,
        data: this.getTab('flow_rules_paths'),
        showCondition: () => this.isReport()
      },
      {
        name: 'period',
        icon: this.tabsMeta['period'].icon,
        data: this.getTab('period'),
        showCondition: () => this.isReport()
      },
    ]
  }

  get complexTabsEnd(): any[] {
    return [
    ];
  }

  get complexTabs() {
    return this.complexTabsBase.filter((tab) => tab.data);
  }
}
